import {
  ADMIN_CHAT,
  CHAT_CONVERSATION_PARAM,
  ChatIcon,
  isInternalRole,
  openDirectConversation,
  openSupportConversation,
  reportError,
  UserPermission,
  usePermissions,
  UserRole,
} from '@vgrubs/components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatButtonStyled } from './OpenConversationButton.styled';

/**
 * Talk to the person a page is about, from that page.
 *
 * Every verification screen ends in a question for whoever submitted it, and
 * until now the only way to ask it was to note the name, leave for the chat
 * page and find them in a picker. This is that trip, as one button.
 *
 * Which kind of thread it opens follows the person's role rather than the
 * screen, because that is what the backend keys on: a colleague is a direct
 * message, an agent and a merchant are both support threads in their own tab.
 * Both endpoints are idempotent - called twice for the same person they hand
 * back the same conversation - so there is nothing to check first and no way to
 * end up with two threads for one user.
 *
 * The scope travels in the link along with the id: the inbox lists one tab at a
 * time, and landing on a thread the list beside it does not contain looks like
 * the page failed to select anything.
 */

type Props = {
  /** Absent while the page is still loading its subject. */
  userId?: number | null;
  role?: UserRole | string | null;
  /** What the person is to this screen: "Owner" on a location, "Agent" on an agent. */
  label?: string;
};

const scopeFor = (role?: UserRole | string | null) => {
  if (isInternalRole(role)) return 'internal';
  return role === UserRole.salesAgent || role === UserRole.salesManager
    ? 'sales'
    : 'merchant';
};

export const OpenConversationButton = ({
  userId,
  role,
  label = 'Chat with Owner',
}: Props) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [busy, setBusy] = useState(false);

  const scope = scopeFor(role);
  const internal = scope === 'internal';

  /* A direct message is gated by membership rather than by a permission -
     everyone internal may start one. A support thread is the inbox's work. */
  const allowed =
    !!userId &&
    can(UserPermission.conversationRead) &&
    (internal || can(UserPermission.conversationWrite));

  if (!allowed) return null;

  const open = async () => {
    if (busy) return;
    try {
      setBusy(true);
      const { data } = internal
        ? await openDirectConversation(userId)
        : await openSupportConversation(userId);
      navigate(
        `${ADMIN_CHAT}?scope=${scope}&` +
          `${CHAT_CONVERSATION_PARAM}=${data.data.id}`
      );
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChatButtonStyled
      $w="auto"
      $wSM="auto"
      $loading={busy}
      onClick={open}
      type="button"
      $selected
    >
      <ChatIcon />
      {label}
    </ChatButtonStyled>
  );
};
